/**
 * career-application-review — the entity surface behind `/applications/:id` (highest AX bar).
 *
 * Binds via the `urn` attribute (listed in `entitySurfaces`, so it MUST carry a valid urn).
 * Deep-linkable COLD: `attach()` fetches `/api/applications/:id` (the durable aggregate + any in-flight
 * GenerationRun), seeds the application store via a synthetic Drafted, subscribes to the application +
 * generation streams (live rail during generation), and `resume()`s from the durable truth. Renders
 * the cover letter as SEMANTIC PARAGRAPHS each with its provenance chips, the six quality gates, the
 * review notes, the Kurzprofil + e-mail, the tailored-CV view, the human-gated edit loop
 * (propose → approve/reject, optimistic), the lifecycle transitions, and a ZIP export. Content is
 * domain data; ALL chrome flows through `t()`.
 */

import {
  KitElement,
  effect,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Router,
  type Signal,
  type Urn,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { seamAutoGrow, segmentParagraph, type MarkSpec } from "@platform/rlhf-elements"
import { ANNOTATION_KINDS, SEVERITIES, type AnnotationKind, type Severity } from "@platform/rlhf-core"
import { skeletonText } from "../lib/skeleton"
import { writeLastGeneration } from "../lib/last-generation"
import { createApplicationEditDraftStore, type ApplicationEditDraftStore } from "../lib/application-edit-draft"
import {
  ApplicationStoreToken,
  GenerationRunStoreToken,
  I18nToken,
  PushToken,
  RouterToken,
  SessionToken,
  apiGet,
  apiPost,
} from "../services"
import { createStreamClaim, type StreamClaim } from "../lib/push-streams"
import type { CareerI18n, CareerKey } from "../../i18n"
import {
  applicationStatusClass,
  careerStream,
  type AnnotationAnchorView,
  type ApplicationAnnotationView,
  type ApplicationGateView,
  type ApplicationStatus,
  type ApplicationStore,
  type ApplicationView,
  type GenerationRunStore,
  type GenerationRunView,
  type ProvenanceEntryView,
  type ShellSession,
} from "../domain"

interface ApplicationDetail {
  application: Record<string, unknown> | null
  generationRun: Record<string, unknown> | null
}
/** What `POST /api/applications/generate` returns (mirrors `career-applications.ts`). */
interface GenerateResponse {
  applicationId: string
  generationId: string
  streamId: string
  applicationStreamId: string
}

/**
 * What the surface is: the run is WORKING (nothing to read yet — show the pipeline), the run
 * FAILED (say so, offer the way out), or there is a result to review. Derived from the folded
 * GenerationRun, never from the presence of letter text — the server seeds a placeholder letter
 * at `Drafted`, so "there is a body" has never meant "there is a letter".
 */
type ReviewPhase = "generating" | "failed" | "ready"

/** The pipeline, in the order the run walks it (`done` is the terminal marker, not a stage). */
const PROGRESS_STAGES = ["preselecting", "authoring", "judging", "kurzprofil", "tailoring", "emailing"] as const
const PROGRESS_STAGE_KEY: Record<(typeof PROGRESS_STAGES)[number], CareerKey> = {
  preselecting: "applications.gen.stage.preselecting",
  authoring: "applications.gen.stage.authoring",
  judging: "applications.gen.stage.judging",
  kurzprofil: "applications.gen.stage.kurzprofil",
  tailoring: "applications.gen.stage.tailoring",
  emailing: "applications.gen.stage.emailing",
}

const GATE_KEY: Record<string, CareerKey> = {
  evidence: "review.gate.evidence",
  roleFit: "review.gate.roleFit",
  motivation: "review.gate.motivation",
  clarity: "review.gate.clarity",
  antiSlop: "review.gate.antiSlop",
  readerTest: "review.gate.readerTest",
}
const SEVERITY_KEY: Record<string, CareerKey> = {
  blocker: "review.severity.blocker",
  major: "review.severity.major",
  minor: "review.severity.minor",
  nit: "review.severity.nit",
  positive: "review.severity.positive",
}
/** Reading order of the notes list: what blocks the letter first, the affirmations last. */
const SEVERITY_ORDER = ["blocker", "major", "minor", "nit", "positive"]

/**
 * ONE Prüfnotiz, placed. `paragraphIndex` + `exact` are present exactly when the finding
 * anchors into a letter paragraph (then it renders as an inline mark); an unanchored finding
 * lives only in the notes list. `key` is the resolve-verb's `id` when the event carries one,
 * otherwise a stable synthetic key — which is ALSO the feature gate: no id ⇒ nothing to
 * resolve against ⇒ no apply/dismiss actions.
 */
interface ReviewFinding {
  key: string
  ann: ApplicationAnnotationView
  paragraphIndex?: number
  exact?: string
}

const isAnnotationKind = (v: string): v is AnnotationKind => (ANNOTATION_KINDS as readonly string[]).includes(v)
const isSeverity = (v: string): v is Severity => (SEVERITIES as readonly string[]).includes(v)

/**
 * Where in the letter BODY the finding's quote sits, as a character offset.
 * Priority: the server's own `start` (verified verbatim, so a stale offset can never
 * mis-place a mark) → the grown `prefix+exact+suffix` context the server made unique →
 * the first occurrence of `exact`. Undefined ⇒ the quote is not in this letter (an
 * applied rewrite already replaced it, say) ⇒ the finding degrades to a notes-list row.
 */
function anchorOffset(body: string, exact: string, anchor: AnnotationAnchorView | undefined): number | undefined {
  const { start, prefix = "", suffix = "" } = anchor ?? {}
  if (start !== undefined && body.slice(start, start + exact.length) === exact) return start
  const context = `${prefix}${exact}${suffix}`
  if (context !== exact) {
    const at = body.indexOf(context)
    if (at >= 0) return at + prefix.length
  }
  const first = body.indexOf(exact)
  return first >= 0 ? first : undefined
}

/** The [start, end) span each trimmed paragraph occupies in the letter body. */
function paragraphRanges(body: string, paras: readonly string[]): { start: number; end: number }[] {
  const ranges: { start: number; end: number }[] = []
  let cursor = 0
  for (const p of paras) {
    const at = body.indexOf(p, cursor)
    const start = at >= 0 ? at : cursor
    ranges.push({ start, end: start + p.length })
    cursor = start + p.length
  }
  return ranges
}

/**
 * Assign each finding to the paragraph that owns its quote.
 *
 * The server anchors at the FIRST occurrence in the body and grows context until it is
 * unique, so mapping the resolved body offset onto the paragraph ranges places the mark
 * exactly — including when the same phrase recurs in a later paragraph. The one residual
 * ambiguity is a quote that repeats WITHIN its own paragraph: `segmentParagraph` places
 * by first-indexOf inside the paragraph it is handed, so the first occurrence wins there.
 * That is accepted (the passage reads the same either way; the comment names the issue).
 */
function placeFindings(body: string, paras: readonly string[], annotations: readonly ApplicationAnnotationView[]): ReviewFinding[] {
  const ranges = paragraphRanges(body, paras)
  return annotations.map((ann, i) => {
    const key = ann.id ?? `${ann.gate}-${ann.severity}-${i}`
    const exact = ann.anchor?.exact ?? ann.span
    if (!exact) return { key, ann }
    const offset = anchorOffset(body, exact, ann.anchor)
    if (offset === undefined) return { key, ann }
    const paragraphIndex = ranges.findIndex((r) => offset >= r.start && offset < r.end)
    // An offset that lands between paragraphs (inside the blank-line gap) still belongs to
    // whichever paragraph actually contains the quote — fall back to that.
    const owner = paragraphIndex >= 0 ? paragraphIndex : paras.findIndex((p) => p.includes(exact))
    return owner >= 0 ? { key, ann, paragraphIndex: owner, exact } : { key, ann }
  })
}

/**
 * German labels for the JSON-Resume section ids the tailoring plan speaks in
 * (`work`, `basics`, `skills`, …) — the review surface must never leak the raw
 * English structural key to the reader. Mirrors `@career/cv-render-v2`'s
 * `DEFAULT_TITLE`; unknown keys fall back to the raw id (still readable).
 */
const SECTION_LABEL: Record<string, string> = {
  basics: "Angaben zur Person",
  summary: "Kurzprofil",
  work: "Berufserfahrung",
  education: "Ausbildung",
  projects: "Projekte",
  skills: "Fähigkeiten",
  languages: "Sprachen",
  certificates: "Zertifikate",
  awards: "Auszeichnungen",
  publications: "Publikationen",
  volunteer: "Ehrenamt",
  interests: "Interessen",
  references: "Referenzen",
}
const sectionLabel = (section: string): string => SECTION_LABEL[section] ?? section

const STATUS_KEY: Record<string, CareerKey> = {
  draft: "applications.status.draft",
  in_review: "applications.status.in_review",
  changes_requested: "applications.status.changes_requested",
  approved: "applications.status.approved",
  sent: "applications.status.sent",
}
/** The legal transitions offered per status (mirrors the api state machine). */
const TRANSITIONS: Record<ApplicationStatus, { to: ApplicationStatus; label: CareerKey }[]> = {
  draft: [{ to: "in_review", label: "review.lifecycle.toReview" }],
  in_review: [
    { to: "approved", label: "review.lifecycle.approve" },
    { to: "changes_requested", label: "review.lifecycle.requestChanges" },
  ],
  changes_requested: [{ to: "in_review", label: "review.lifecycle.toReview" }],
  approved: [
    { to: "sent", label: "review.lifecycle.markSent" },
    { to: "in_review", label: "review.lifecycle.reopen" },
  ],
  sent: [],
}

export class ApplicationReviewVm implements ViewModel {
  readonly app: ReadonlySignal<ApplicationView | undefined>
  readonly genRun: ReadonlySignal<GenerationRunView | undefined>
  readonly editing: Signal<{ index: number; text: string } | null> = signal<{ index: number; text: string } | null>(null)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  /** A retry of a failed run is in flight (the button owns its own pending state). */
  readonly retrying: Signal<boolean> = signal(false)
  /** The ONE Prüfnotiz whose finding block is open, by `ReviewFinding.key` (focused UI). */
  readonly openAnnotation: Signal<string | null> = signal<string | null>(null)
  /** The finding whose apply/dismiss chain is in flight (its buttons own the pending state). */
  readonly resolvingAnnotation: Signal<string | null> = signal<string | null>(null)
  /**
   * The resolve verb answered 404 — this application's annotations predate the ids it joins on.
   * Latched for the session: the finding stays visually unresolved and stops offering actions
   * it cannot honour. NOT an error the reader should read; nothing was broken by them.
   */
  readonly resolveUnavailable: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  readonly urn: Urn
  private readonly shortId: string
  private seq = 1
  private readonly streams = new Set<string>()
  /** Tear-down for the genStore→genRun reactive mirror (set once the run id is known). */
  private stopGenMirror: (() => void) | null = null
  /** Per-user, per-application backup of the open paragraph rewrite — see `application-edit-draft`. */
  private readonly editDraft: ApplicationEditDraftStore

  /** This surface's claim on the shared push channel (`lib/push-streams.ts`): the channel
   *  subscription is the UNION of every mounted surface's claim, never just ours. */
  private readonly claim: StreamClaim

  constructor(
    private readonly store: ApplicationStore,
    private readonly genStore: GenerationRunStore,
    push: PushChannel,
    private readonly router: Router,
    private readonly session: ShellSession,
    i18n: CareerI18n,
    urn: Urn,
  ) {
    this.t = i18n.t
    this.claim = createStreamClaim(push)
    this.urn = urn
    this.shortId = urn.replace(/^urn:career:application:/, "")
    this.editDraft = createApplicationEditDraftStore(urn, session.user?.sub ?? null)
    this.app = store.entity(urn)
    this.genRun = signal<GenerationRunView | undefined>(undefined)
    this.renderDeps = [
      this.app,
      this.genRun,
      this.editing,
      this.error,
      this.loaded,
      this.retrying,
      this.openAnnotation,
      this.resolvingAnnotation,
      this.resolveUnavailable,
      store.pending,
    ]
  }

  private get userId(): string | null {
    return this.session.user?.sub ?? null
  }

  attach(): () => void {
    // Restore the open rewrite BEFORE the server read — it must rescue the text even when the
    // hydrate fails outright (offline, or the session that just expired). A draft only exists
    // while an edit is unproposed (`saveEdit`/`cancelEdit` clear it), so it is always the
    // freshest truth about what the reader was writing.
    const draft = this.editDraft.load()
    if (draft) this.editing.set({ index: draft.index, text: draft.text })
    void this.hydrate()
    return () => {
      this.stopGenMirror?.()
      this.claim.release()
    }
  }

  /**
   * What this surface is right now. The run rail is the ONLY honest source: a running run
   * means "nothing to read yet", a failed run means "say so", anything else means the folded
   * application IS the result. (The old gate — "no letter body yet" — was false from the very
   * first render, because the server seeds a placeholder body.)
   */
  phase(): ReviewPhase {
    const status = this.genRun.value?.status
    if (status === "running") return "generating"
    if (status === "failed") return "failed"
    return "ready"
  }

  /**
   * Start the generation over against the SAME opportunity — the one sensible next step after a
   * failed run. Re-uses the existing verb; the response's two streams are remembered (so a cold
   * load resumes them, see `lib/last-generation.ts`) and subscribed before we navigate to the
   * new application, so the rail plays from the first event.
   */
  async retry(): Promise<void> {
    const opportunityRef = this.app.value?.opportunityRef
    if (!opportunityRef || this.retrying.value) return
    this.retrying.set(true)
    this.error.set(null)
    try {
      const res = await apiPost<GenerateResponse>("/api/applications/generate", { opportunityId: opportunityRef.replace(/^urn:career:opportunity:/, "") })
      writeLastGeneration(this.userId, { streamId: res.streamId, applicationStreamId: res.applicationStreamId })
      this.streams.add(res.streamId)
      this.streams.add(res.applicationStreamId)
      this.claim.claim([...this.streams])
      void this.router.navigate(`/applications/${res.applicationId.replace(/^urn:career:application:/, "")}`)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.retrying.set(false)
    }
  }

  /** Paragraphs of the letter body (blank-line split — matches the server segmenter). */
  paragraphs(a: ApplicationView): string[] {
    return a.letterBody.split(/\n{2,}/).map((p) => p.trim()).filter((p) => p.length > 0)
  }

  /** The provenance entries whose anchor falls inside this paragraph → its claim chips. */
  paragraphProvenance(a: ApplicationView, paragraph: string): ProvenanceEntryView[] {
    return a.letterProvenance.filter((e) => e.anchor && paragraph.includes(e.anchor))
  }

  /** The current Prüfnotizen (final critique pass — the projection guarantees it), each placed. */
  findings(a: ApplicationView): ReviewFinding[] {
    return placeFindings(a.letterBody, this.paragraphs(a), a.annotations)
  }

  /** Open the named finding, or close it when it is the one already open (focused UI: one at a time). */
  toggleAnnotation(key: string): void {
    this.openAnnotation.set(this.openAnnotation.value === key ? null : key)
  }

  /**
   * Take the model's rewrite through the SAME human-gated edit loop a manual change uses:
   * propose the paragraph with the anchored passage replaced → approve it → record the finding
   * as `applied`. Sequential by design — the resolve joins on an edit that has already landed.
   */
  async applyRewrite(f: ReviewFinding): Promise<void> {
    const a = this.app.value
    const { ann } = f
    if (!a || !ann.id || !ann.suggestedRewrite || f.paragraphIndex === undefined || !f.exact) return
    if (this.resolvingAnnotation.value) return
    const paragraph = this.paragraphs(a)[f.paragraphIndex]
    if (paragraph === undefined || !paragraph.includes(f.exact)) return
    // Replace the anchored passage ONLY (function replacer — a `$` in the rewrite is literal).
    const rewrite = ann.suggestedRewrite
    const after = paragraph.replace(f.exact, () => rewrite)
    this.resolvingAnnotation.set(f.key)
    this.error.set(null)
    try {
      const { editId } = await apiPost<{ editId: string }>("/api/applications/edit/propose", {
        applicationId: this.urn,
        target: { kind: "cover_letter_paragraph", paragraphIndex: f.paragraphIndex },
        after,
        ...(ann.anchor ? { anchor: ann.anchor } : {}),
        rationale: ann.comment,
      })
      await apiPost("/api/applications/edit/approve", { applicationId: this.urn, editId })
      await this.resolveAnnotation(ann.id, "applied")
      this.openAnnotation.set(null)
      await this.hydrate()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.resolvingAnnotation.set(null)
    }
  }

  /** "Verwerfen" — the finding is recorded as dismissed; the letter is untouched. */
  async dismissFinding(f: ReviewFinding): Promise<void> {
    const { ann } = f
    if (!ann.id || this.resolvingAnnotation.value) return
    this.resolvingAnnotation.set(f.key)
    this.error.set(null)
    try {
      await this.resolveAnnotation(ann.id, "dismissed")
      this.openAnnotation.set(null)
      await this.hydrate()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.resolvingAnnotation.set(null)
    }
  }

  /**
   * Record one finding's resolution. A 404 means this application's annotations were written
   * before the server generated the ids the verb joins on — nothing the reader did, and nothing
   * they can fix: latch the verb off for the session and stay silent (the finding simply keeps
   * reading unresolved). Every other failure is a real error and propagates to `error`.
   */
  private async resolveAnnotation(annotationId: string, resolution: "applied" | "dismissed"): Promise<void> {
    if (this.resolveUnavailable.value) return
    try {
      await apiPost("/api/applications/annotation/resolve", { applicationId: this.urn, annotationId, resolution })
    } catch (e) {
      if (e instanceof Error && /HTTP 404$/.test(e.message)) {
        this.resolveUnavailable.set(true)
        return
      }
      throw e
    }
  }

  /**
   * The draft is kept in LOCKSTEP with `editing`: it backs up exactly what the editor holds
   * and nothing else. Opening a paragraph seeds the box with the letter's own prose — nothing
   * typed yet — so it drops any older draft rather than resurrecting an edit the single-slot
   * editor has already walked away from.
   */
  startEdit(index: number, text: string): void {
    this.editDraft.clear()
    this.editing.set({ index, text })
  }
  cancelEdit(): void {
    this.editDraft.clear()
    this.editing.set(null)
  }
  setEditText(text: string): void {
    const cur = this.editing.value
    if (!cur) return
    this.editing.set({ ...cur, text })
    // This rewrite exists NOWHERE else until »Vorschlagen« — back it up before a reload or the
    // 401 route-to-login round-trip can end the page's life.
    this.editDraft.save({ index: cur.index, text, updatedAt: new Date().toISOString() })
  }

  async saveEdit(): Promise<void> {
    const cur = this.editing.value
    if (!cur || cur.text.trim().length === 0) return
    this.error.set(null)
    try {
      await apiPost("/api/applications/edit/propose", { applicationId: this.urn, target: { kind: "cover_letter_paragraph", paragraphIndex: cur.index }, after: cur.text.trim() })
      // The rewrite is durable server-side now — the local rescue copy has done its job.
      this.editDraft.clear()
      this.editing.set(null)
      await this.hydrate()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    }
  }

  approveEdit(editId: string): void {
    void this.store.command({ type: "edit-approve", payload: { applicationId: this.urn, editId } }).then(() => this.hydrate())
  }
  rejectEdit(editId: string): void {
    void this.store.command({ type: "edit-reject", payload: { applicationId: this.urn, editId } }).then(() => this.hydrate())
  }
  transition(to: ApplicationStatus): void {
    void this.store.command({ type: "transition", payload: { applicationId: this.urn, to } }).then(() => this.hydrate())
  }

  go(url: string): void {
    void this.router.navigate(url)
  }

  private async hydrate(): Promise<void> {
    const userId = this.userId
    try {
      const { application, generationRun } = await apiGet<ApplicationDetail>(`/api/applications/${this.shortId}`)
      if (userId && application && typeof application["id"] === "string") {
        this.store.apply({ streamId: careerStream(userId, "Application", application["id"]), version: this.seq++, type: "application.Drafted", payload: { owner: userId, application } })
        this.streams.add(careerStream(userId, "Application", application["id"]))
      }
      if (userId && generationRun && typeof generationRun["id"] === "string") {
        const genId = generationRun["id"]
        const genStream = careerStream(userId, "GenerationRun", genId)
        // Seed the gen rail from the folded run + subscribe live (resume() heals SSE drops).
        this.genStore.apply({ streamId: genStream, version: this.seq++, type: "generationRun.Started", payload: { owner: userId, generationId: genId, applicationRef: generationRun["applicationRef"] ?? "", startedAt: generationRun["startedAt"] ?? "" } })
        this.genStore.apply({ streamId: genStream, version: this.seq++, type: "generationRun.Progressed", payload: { owner: userId, generationId: genId, stage: generationRun["stage"] ?? "preselecting", iterations: generationRun["iterations"] ?? 0, costUsd: generationRun["costUsd"] ?? 0, at: generationRun["updatedAt"] ?? "" } })
        if (generationRun["status"] === "completed") {
          this.genStore.apply({ streamId: genStream, version: this.seq++, type: "generationRun.Completed", payload: { owner: userId, generationId: genId, converged: generationRun["converged"] ?? false, costUsd: generationRun["costUsd"] ?? 0, at: generationRun["updatedAt"] ?? "" } })
        }
        // …and the TERMINAL failure/stop the same way. Without these the seed above leaves every
        // cold-loaded run reading "running", so a deep link to a run that FAILED (the durable
        // truth `/api/applications/:id` just handed us, error and all) would show an endless
        // progress timeline and never the reason it stopped.
        if (generationRun["status"] === "failed") {
          this.genStore.apply({ streamId: genStream, version: this.seq++, type: "generationRun.Failed", payload: { owner: userId, generationId: genId, error: generationRun["error"] ?? "", at: generationRun["updatedAt"] ?? "" } })
        }
        if (generationRun["status"] === "stopped") {
          this.genStore.apply({ streamId: genStream, version: this.seq++, type: "generationRun.Stopped", payload: { owner: userId, generationId: genId, at: generationRun["updatedAt"] ?? "" } })
        }
        // Mirror the store's REACTIVE entity signal into genRun so live folds (the
        // generationRun.Completed that arrives over push when the letter lands) update
        // the rail — a one-time `.value` copy would freeze the run at "running" and the
        // progress banner would never clear once the letter rendered.
        const genEntity = this.genStore.entity(genId as Urn)
        this.stopGenMirror?.()
        this.stopGenMirror = effect([genEntity], ([v]) => (this.genRun as Signal<GenerationRunView | undefined>).set(v))
        this.streams.add(genStream)
        if (generationRun["status"] === "running") this.claim.claim([...this.streams])
      }
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }
}

export class CareerApplicationReview extends KitElement<ApplicationReviewVm> {
  protected createViewModel(ctx: KitContext): ApplicationReviewVm {
    const { urn } = this
    if (!urn) throw new Error("[career] <career-application-review> needs a urn attribute.")
    return new ApplicationReviewVm(
      ctx.inject(ApplicationStoreToken),
      ctx.inject(GenerationRunStoreToken),
      ctx.inject(PushToken),
      ctx.inject(RouterToken),
      ctx.inject(SessionToken),
      ctx.inject(I18nToken),
      urn,
    )
  }

  private letterPanel(vm: ApplicationReviewVm, a: ApplicationView, findings: readonly ReviewFinding[]): TemplateResult {
    const paras = vm.paragraphs(a)
    const editing = vm.editing.value
    return html`
      <section class=${classMap({ "review-letter": true, "surface-panel": true, "is-degraded": a.letterDegraded })} aria-labelledby="review-letter-title">
        <h2 id="review-letter-title" class="surface-section-title" lang="de">${vm.t("review.letter.title")}</h2>
        ${a.letterDegraded ? html`<p class="review-degraded-cue" role="status" lang="de">${vm.t("review.letter.degraded")}</p>` : nothing}
        ${a.letterSubject ? html`<p class="review-letter-subject" lang="de">${a.letterSubject}</p>` : nothing}
        ${a.letterSalutation ? html`<p class="review-letter-salutation" lang="de">${a.letterSalutation}</p>` : nothing}
        <ol class="review-letter-paragraphs" role="list">
          ${paras.map((p, i) => this.paragraph(vm, a, p, i, editing, findings.filter((f) => f.paragraphIndex === i)))}
        </ol>
        ${a.letterClosing ? html`<p class="review-letter-closing" lang="de">${a.letterClosing}</p>` : nothing}
        ${a.letterEnclosure ? html`<p class="review-letter-enclosure" lang="de">${a.letterEnclosure}</p>` : nothing}
      </section>
    `
  }

  private paragraph(
    vm: ApplicationReviewVm,
    a: ApplicationView,
    p: string,
    i: number,
    editing: { index: number; text: string } | null,
    marked: readonly ReviewFinding[],
  ): TemplateResult {
    const prov = vm.paragraphProvenance(a, p)
    const refs = [...new Set(prov.flatMap((e) => e.refs))]
    if (editing && editing.index === i) {
      return html`
        <li class="review-paragraph is-editing">
          <textarea class="review-edit-text" aria-label=${vm.t("review.edit.propose")} ${seamAutoGrow()} @input=${(e: Event) => vm.setEditText((e.target as HTMLTextAreaElement).value)} lang="de">${editing.text}</textarea>
          <div class="review-edit-actions" role="group">
            <button data-act="edit-save" class="review-edit-save" lang="de">${vm.t("review.edit.save")}</button>
            <button data-act="edit-cancel" class="review-edit-cancel" lang="de">${vm.t("review.edit.cancel")}</button>
          </div>
        </li>
      `
    }
    const open = marked.find((f) => f.key === vm.openAnnotation.value)
    return html`
      <li class="review-paragraph">
        <p class="review-paragraph-text" lang="de">${this.paragraphText(vm, p, marked)}</p>
        ${open ? this.findingBlock(vm, open) : nothing}
        <div class="review-paragraph-meta">
          ${refs.length > 0
            ? html`<ul class="review-paragraph-provenance" role="list" aria-label=${vm.t("review.narrative.provenance", { n: refs.length })}>
                ${refs.map((r) => html`<li class="review-claim-chip chip" urn=${r} lang="de">${vm.t("review.narrative.provenance", { n: 1 })}</li>`)}
              </ul>`
            : nothing}
          <button data-act="propose-edit" data-paragraph=${String(i)} class="review-edit-open" lang="de">${vm.t("review.edit.propose")}</button>
        </div>
      </li>
    `
  }

  /**
   * The paragraph's prose with its Prüfnotizen marked IN it. Segmentation is the shared
   * `segmentParagraph` from the RLHF elements (one placement algorithm across the platform);
   * the mark itself is a real <button> so it is keyboard-reachable and AX-3-legal — the
   * <mark> inside carries the semantics and the severity styling.
   */
  private paragraphText(vm: ApplicationReviewVm, p: string, marked: readonly ReviewFinding[]): TemplateResult[] | string {
    const marks: MarkSpec[] = marked.flatMap((f) =>
      f.exact && isAnnotationKind(f.ann.kind) && isSeverity(f.ann.severity)
        ? [{ id: f.key, exact: f.exact, kind: f.ann.kind, severity: f.ann.severity }]
        : [],
    )
    if (marks.length === 0) return p
    const byKey = new Map(marked.map((f) => [f.key, f]))
    return segmentParagraph(p, marks).map((seg) => {
      const f = seg.mark ? byKey.get(seg.mark.id) : undefined
      if (!seg.mark || !f) return html`${seg.text}`
      const isOpen = vm.openAnnotation.value === f.key
      return html`<button
        type="button"
        class="review-mark-hit"
        data-act="open-annotation"
        data-ann=${f.key}
        aria-expanded=${isOpen ? "true" : "false"}
        aria-controls=${isOpen ? `review-finding-${f.key}` : nothing}
      ><mark
        class=${classMap({ "review-mark": true, [`is-${seg.mark.severity}`]: true, "is-resolved": Boolean(f.ann.resolved) })}
        data-ann=${f.key}
        lang="de"
      >${seg.text}</mark></button>`
    })
  }

  /**
   * ONE finding, disclosed in the document flow (§0 — a hairline rule in the severity color,
   * never a floating popover). What it says, what it proposes instead, and the two answers the
   * reader can give it. The actions appear only where they can actually be honoured: the
   * finding must carry the server id the resolve verb joins on, the rewrite must have a place
   * to land, and a `positive` finding is an affirmation — there is nothing to act on.
   */
  private findingBlock(vm: ApplicationReviewVm, f: ReviewFinding): TemplateResult {
    const { ann } = f
    const gate = vm.t(GATE_KEY[ann.gate] ?? "review.gate.clarity")
    const resolved = Boolean(ann.resolved)
    const pending = vm.resolvingAnnotation.value === f.key
    const busy = vm.resolvingAnnotation.value !== null
    const actionable = Boolean(ann.id) && !resolved && ann.severity !== "positive" && !vm.resolveUnavailable.value
    const applicable = actionable && Boolean(ann.suggestedRewrite) && f.paragraphIndex !== undefined && Boolean(f.exact)
    return html`
      <div
        id=${`review-finding-${f.key}`}
        class=${classMap({ "review-finding": true, [`is-${ann.severity}`]: Boolean(SEVERITY_KEY[ann.severity]), "is-resolved": resolved })}
        role="group"
        aria-label=${vm.t("review.finding.label", { gate })}
      >
        <p class="review-finding-meta">
          <span class="review-finding-severity chip" lang="de">${vm.t(SEVERITY_KEY[ann.severity] ?? "review.severity.minor")}</span>
          <span class="review-finding-gate" lang="de">${gate}</span>
          ${resolved ? html`<span class="review-finding-state" lang="de">${vm.t("review.finding.resolved")}</span>` : nothing}
        </p>
        <p class="review-finding-comment" lang="de">${ann.comment}</p>
        ${ann.suggestedRewrite
          ? html`<figure class="review-finding-rewrite">
              <figcaption class="review-finding-rewrite-label" lang="de">${vm.t("review.finding.suggestion")}</figcaption>
              <blockquote class="review-finding-rewrite-text" lang="de">${ann.suggestedRewrite}</blockquote>
            </figure>`
          : nothing}
        ${actionable
          ? html`<div class="review-finding-actions" role="group" aria-label=${vm.t("review.finding.label", { gate })}>
              ${applicable
                ? html`<button
                    data-act="apply-rewrite"
                    data-ann=${f.key}
                    class=${classMap({ "review-finding-apply": true, "btn-primary": true, "is-pending": pending })}
                    ?disabled=${busy}
                    lang="de"
                  >${pending ? vm.t("review.finding.applying") : vm.t("review.finding.apply")}</button>`
                : nothing}
              <button data-act="dismiss-annotation" data-ann=${f.key} class="review-finding-dismiss" ?disabled=${busy} lang="de">
                ${vm.t("review.finding.dismiss")}
              </button>
            </div>`
          : nothing}
      </div>
    `
  }

  /**
   * The Prüfnotizen as a list, under the letter: every current finding in severity order, one
   * line each. An anchored row opens (and scrolls to) its mark in the letter; an unanchored one
   * — the model could not point at a passage — expands right here, with the same block and the
   * same answers. Nothing at all to report renders nothing at all.
   */
  private notesSection(vm: ApplicationReviewVm, findings: readonly ReviewFinding[]): TemplateResult | typeof nothing {
    if (findings.length === 0) return nothing
    const ordered = [...findings].sort((x, y) => SEVERITY_ORDER.indexOf(x.ann.severity) - SEVERITY_ORDER.indexOf(y.ann.severity))
    return html`
      <section class="review-notes" aria-labelledby="review-notes-title">
        <h2 id="review-notes-title" class="surface-section-title" lang="de">${vm.t("review.annotations.title")}</h2>
        <ul class="review-note-list" role="list">
          ${repeat(ordered, (f) => f.key, (f) => this.noteRow(vm, f))}
        </ul>
      </section>
    `
  }

  private noteRow(vm: ApplicationReviewVm, f: ReviewFinding): TemplateResult {
    const { ann } = f
    const isOpen = vm.openAnnotation.value === f.key
    const anchored = f.paragraphIndex !== undefined
    return html`
      <li
        class=${classMap({
          "review-note": true,
          [`is-${ann.severity}`]: Boolean(SEVERITY_KEY[ann.severity]),
          "is-resolved": Boolean(ann.resolved),
          "is-open": isOpen,
        })}
      >
        <button
          type="button"
          class="review-note-open"
          data-act="open-annotation"
          data-ann=${f.key}
          aria-expanded=${isOpen ? "true" : "false"}
          aria-controls=${isOpen ? `review-finding-${f.key}` : nothing}
        >
          <span class="review-note-severity chip" lang="de">${vm.t(SEVERITY_KEY[ann.severity] ?? "review.severity.minor")}</span>
          <span class="review-note-gate" lang="de">${vm.t(GATE_KEY[ann.gate] ?? "review.gate.clarity")}</span>
          <span class="review-note-comment" lang="de">${ann.comment}</span>
        </button>
        ${isOpen && !anchored ? this.findingBlock(vm, f) : nothing}
      </li>
    `
  }

  private gate(vm: ApplicationReviewVm, g: ApplicationGateView): TemplateResult {
    return html`
      <li class=${classMap({ "review-gate": true, "is-completed": g.pass, "is-failed": !g.pass })}>
        <span class="review-gate-name" lang="de">${vm.t(GATE_KEY[g.gate] ?? "review.gate.clarity")}</span>
        <span class="review-gate-verdict chip" lang="de">${vm.t(g.pass ? "review.gate.pass" : "review.gate.fail")}</span>
      </li>
    `
  }

  private gatesPanel(vm: ApplicationReviewVm, a: ApplicationView): TemplateResult | typeof nothing {
    if (a.gates.length === 0) return nothing
    return html`
      <section class="review-gates surface-panel" aria-labelledby="review-gates-title">
        <h2 id="review-gates-title" class="surface-section-title" lang="de">${vm.t("review.gates.title")}</h2>
        <ul class="review-gate-list" role="list">${a.gates.map((g) => this.gate(vm, g))}</ul>
      </section>
    `
  }

  private kurzprofilPanel(vm: ApplicationReviewVm, a: ApplicationView): TemplateResult | typeof nothing {
    if (!a.kurzprofilBody) return nothing
    return html`
      <section class=${classMap({ "review-kurzprofil": true, "surface-panel": true, "is-degraded": a.kurzprofilDegraded })} aria-labelledby="review-kurzprofil-title">
        <h2 id="review-kurzprofil-title" class="surface-section-title" lang="de">${vm.t("review.kurzprofil.title")}</h2>
        ${a.kurzprofilDegraded ? html`<p class="review-degraded-cue" role="status" lang="de">${vm.t("review.letter.degraded")}</p>` : nothing}
        <p class="review-kurzprofil-text" lang="de">${a.kurzprofilBody}</p>
      </section>
    `
  }

  private emailPanel(vm: ApplicationReviewVm, a: ApplicationView): TemplateResult | typeof nothing {
    if (!a.emailNote) return nothing
    return html`
      <section class="review-email surface-panel" aria-labelledby="review-email-title">
        <h2 id="review-email-title" class="surface-section-title" lang="de">${vm.t("review.email.title")}</h2>
        <p class="review-email-text" lang="de">${a.emailNote}</p>
      </section>
    `
  }

  private tailoringPanel(vm: ApplicationReviewVm, a: ApplicationView): TemplateResult | typeof nothing {
    if (a.tailoringSections.length === 0) return nothing
    const sections = [...a.tailoringSections].filter((s) => s.include).sort((x, y) => (x.order ?? 0) - (y.order ?? 0))
    return html`
      <section class="review-tailoring surface-panel" aria-labelledby="review-tailoring-title">
        <h2 id="review-tailoring-title" class="surface-section-title" lang="de">${vm.t("review.tailoring.title")}</h2>
        <ul class="review-tailoring-list" role="list">
          ${sections.map(
            (s) => html`<li class="review-tailoring-section" lang="de">
              <span class="review-tailoring-name">${sectionLabel(s.section)}</span>
              ${s.emphasis ? html`<span class="review-tailoring-what" lang="de">${s.emphasis}</span>` : nothing}
            </li>`,
          )}
        </ul>
      </section>
    `
  }

  private editsPanel(vm: ApplicationReviewVm, a: ApplicationView): TemplateResult | typeof nothing {
    const pending = a.proposedEdits.filter((e) => e.status === "proposed")
    if (pending.length === 0) return nothing
    return html`
      <section class="review-edits surface-panel" aria-labelledby="review-edits-title">
        <h2 id="review-edits-title" class="surface-section-title" lang="de">${vm.t("review.edit.pending")}</h2>
        <ul class="review-edit-list" role="list">
          ${repeat(pending, (e) => e.id, (e) => html`
            <li class="review-edit is-pending" data-edit=${e.id}>
              <p class="review-edit-after" lang="de">${e.after}</p>
              <div class="review-edit-actions" role="group">
                <button data-act="edit-approve" data-edit=${e.id} class="review-edit-approve" lang="de">${vm.t("review.edit.approve")}</button>
                <button data-act="edit-reject" data-edit=${e.id} class="review-edit-reject" lang="de">${vm.t("review.edit.reject")}</button>
              </div>
            </li>`)}
        </ul>
      </section>
    `
  }

  /**
   * The run in flight, rendered as the document itself (§0 — a hairline rule and typography,
   * no panel box): a lead line, then the six pipeline stages as a vertical timeline. Completed
   * stages fall back to quiet type with a check, the current one carries the weight plus a
   * pulsing activity dot, everything ahead stays dimmed. The review-round chip appears from the
   * SECOND round on — one pass is not news, a second one is.
   */
  private progressBlock(vm: ApplicationReviewVm, run: GenerationRunView): TemplateResult {
    const at = PROGRESS_STAGES.indexOf(run.stage as (typeof PROGRESS_STAGES)[number])
    return html`
      <section class="review-progress" role="status" aria-live="polite" aria-label=${vm.t("review.progress.label")}>
        <p class="review-progress-lead" lang="de">${vm.t("review.progress.lead")}</p>
        <ol class="review-progress-steps" role="list">
          ${PROGRESS_STAGES.map((stage, i) => {
            const done = at < 0 || i < at
            const current = i === at
            return html`
              <li class=${classMap({ "review-progress-step": true, "is-done": done, "is-current": current, "is-todo": !done && !current })}>
                <span class="review-progress-mark" aria-hidden="true">${done ? "✓" : current ? html`<span class="gen-dot"></span>` : nothing}</span>
                <span class="review-progress-label" lang="de">${vm.t(PROGRESS_STAGE_KEY[stage])}</span>
                ${current && run.iterations > 1
                  ? html`<span class="review-progress-rounds chip" lang="de">${vm.t("applications.gen.iterations", { n: run.iterations })}</span>`
                  : nothing}
              </li>
            `
          })}
        </ol>
      </section>
    `
  }

  /**
   * The run failed. Calm, not alarming (§0): a `--critical` left rule, the honest heading, the
   * technical message as quiet secondary text, and the one useful next step — run it again
   * against the same job — plus the quiet way back.
   */
  private failureBlock(vm: ApplicationReviewVm, a: ApplicationView, run: GenerationRunView): TemplateResult {
    const opportunity = a.opportunityRef?.replace(/^urn:career:opportunity:/, "")
    return html`
      <section class="review-failed" role="alert" aria-labelledby="review-failed-title">
        <h2 id="review-failed-title" class="review-failed-title" lang="de">${vm.t("review.failed.title")}</h2>
        <p class="review-failed-lead" lang="de">${vm.t("review.failed.lead")}</p>
        ${run.error ? html`<p class="review-failed-detail" lang="de">${vm.t("review.failed.detail", { message: run.error })}</p>` : nothing}
        <div class="review-failed-actions" role="group" aria-label=${vm.t("review.failed.title")}>
          ${opportunity
            ? html`<button
                data-act="retry-generation"
                data-opportunity=${opportunity}
                class=${classMap({ "review-retry": true, "btn-primary": true, "is-pending": vm.retrying.value })}
                ?disabled=${vm.retrying.value}
                lang="de"
              >${vm.retrying.value ? vm.t("review.failed.retrying") : vm.t("review.failed.retry")}</button>`
            : nothing}
          <a class="review-failed-back" href="/applications" data-act="nav" data-url="/applications" lang="de">${vm.t("review.back")}</a>
        </div>
      </section>
    `
  }

  private lifecyclePanel(vm: ApplicationReviewVm, a: ApplicationView): TemplateResult {
    const next = TRANSITIONS[a.status] ?? []
    return html`
      <section class="review-lifecycle surface-panel" aria-labelledby="review-lifecycle-title">
        <h2 id="review-lifecycle-title" class="surface-section-title" lang="de">${vm.t("review.lifecycle.title")}</h2>
        <p class=${classMap({ "review-lifecycle-current": true, chip: true, [applicationStatusClass(a.status)]: true })} lang="de">${vm.t(STATUS_KEY[a.status] ?? "applications.status.draft")}</p>
        <div class="review-lifecycle-actions" role="group">
          ${next.map((n) => html`<button data-act="transition" data-application=${vm.urn} data-to=${n.to} class="review-transition" lang="de">${vm.t(n.label)}</button>`)}
          <a class="review-download chip" href=${`/api/applications/${vm.urn.replace(/^urn:career:application:/, "")}/package.zip`} data-act="download-zip" data-application=${vm.urn} lang="de">${vm.t("review.download")}</a>
        </div>
      </section>
    `
  }

  protected render(vm: ApplicationReviewVm): TemplateResult {
    const a = vm.app.value
    const run = vm.genRun.value
    const back = html`<a class="review-back chip" href="/applications" data-act="nav" data-url="/applications" lang="de">${vm.t("review.back")}</a>`

    if (!a) {
      const gone = vm.loaded.value
      return html`
        <section class="surface application-review" urn=${vm.urn} aria-labelledby="review-title">
          <h1 id="review-title" class="surface-title" lang="de">${vm.t("surface.bewerbungen.title")}</h1>
          ${vm.error.value ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: vm.error.value })}</p>` : nothing}
          ${gone ? html`<p class="surface-empty" lang="de">${vm.t("review.notFound")}</p>` : nothing}
          ${!gone && !vm.error.value ? html`<div class="review-loading" aria-busy="true" aria-label=${vm.t("review.generating")}>${skeletonText(5)}</div>` : nothing}
          ${back}
        </section>
      `
    }

    // The run rail tells the surface what it is (see `phase()`). While the run works, the
    // pipeline stands where the letter will be; on failure the error stands under the title.
    // The letter and its quality gates appear ONLY once a real letter has been authored —
    // the seeded placeholder body must never render as prose the user could edit, and six
    // red gates on a letter that does not exist yet is not a verdict, it is noise.
    const phase = vm.phase()
    const generating = phase === "generating"
    const showResult = phase === "ready" || a.letterAuthored
    // Placed ONCE per render: the letter's marks and the notes list are two views of the
    // same findings, so they must agree on which paragraph owns which note.
    const findings = showResult ? vm.findings(a) : []
    return html`
      <section
        class=${classMap({ surface: true, "application-review": true, [applicationStatusClass(a.status)]: true, "is-running": generating, "is-failed": phase === "failed", "is-degraded": a.letterDegraded })}
        urn=${vm.urn}
        aria-labelledby="review-title"
      >
        <h1 id="review-title" class="surface-title" lang="de">${a.letterSubject ?? vm.t("surface.bewerbungen.title")}</h1>
        ${vm.error.value ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: vm.error.value })}</p>` : nothing}

        ${phase === "failed" && run ? this.failureBlock(vm, a, run) : nothing}
        ${generating && run ? this.progressBlock(vm, run) : nothing}

        ${showResult ? this.letterPanel(vm, a, findings) : nothing}
        ${showResult ? this.notesSection(vm, findings) : nothing}
        ${showResult ? this.gatesPanel(vm, a) : nothing}
        ${this.kurzprofilPanel(vm, a)}
        ${this.emailPanel(vm, a)}
        ${this.tailoringPanel(vm, a)}
        ${generating ? nothing : this.editsPanel(vm, a)}
        ${generating ? nothing : this.lifecyclePanel(vm, a)}
        ${back}
      </section>
    `
  }

  /** Re-place the findings and pick the one this affordance names (the DOM carries only its key). */
  private finding(vm: ApplicationReviewVm, key: string): ReviewFinding | undefined {
    const a = vm.app.value
    return a ? vm.findings(a).find((f) => f.key === key) : undefined
  }

  /**
   * Bring the just-opened finding's mark into view — a note opened from the list under the
   * letter points at a passage that may be a screen away. Best-effort: no mark (unanchored
   * finding) or no `scrollIntoView` (non-browser render) simply does nothing.
   */
  private scrollToMark(key: string): void {
    if (typeof requestAnimationFrame !== "function") return
    requestAnimationFrame(() => {
      const mark = [...this.querySelectorAll(".review-mark")].find((m) => m.getAttribute("data-ann") === key)
      if (mark instanceof HTMLElement && typeof mark.scrollIntoView === "function") {
        mark.scrollIntoView({ block: "center", behavior: "smooth" })
      }
    })
  }

  protected override actions(vm: ApplicationReviewVm): ActionMap {
    return {
      "propose-edit": ({ paragraph }) => {
        const a = vm.app.value
        if (!a || paragraph === undefined) return
        const idx = Number(paragraph)
        vm.startEdit(idx, vm.paragraphs(a)[idx] ?? "")
      },
      // A Prüfnotiz: open it (from its mark, or from its row in the notes list — the same ONE
      // block either way), then take its rewrite or dismiss it.
      "open-annotation": ({ ann }) => {
        if (!ann) return
        vm.toggleAnnotation(ann)
        if (vm.openAnnotation.value === ann) this.scrollToMark(ann)
      },
      "apply-rewrite": ({ ann }) => {
        const f = ann ? this.finding(vm, ann) : undefined
        if (f) void vm.applyRewrite(f)
      },
      "dismiss-annotation": ({ ann }) => {
        const f = ann ? this.finding(vm, ann) : undefined
        if (f) void vm.dismissFinding(f)
      },
      "edit-save": () => void vm.saveEdit(),
      "edit-cancel": () => vm.cancelEdit(),
      "edit-approve": ({ edit }) => {
        if (edit) vm.approveEdit(edit)
      },
      "edit-reject": ({ edit }) => {
        if (edit) vm.rejectEdit(edit)
      },
      transition: ({ to }) => {
        if (to) vm.transition(to as ApplicationStatus)
      },
      "retry-generation": () => void vm.retry(),
      "download-zip": () => {
        /* native anchor navigation performs the download; nothing to intercept */
      },
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
